import { useState, useEffect, useRef } from 'react';
import { classNames } from 'primereact/utils';
import { Controller, useForm } from 'react-hook-form';
import { DataTable, DataTableFilterMeta, DataTableSelectionSingleChangeEvent } from 'primereact/datatable';
import { Column, ColumnEditorOptions } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { MaterialUsageService } from '../../service/MaterialUsageService';
import { RawMaterialGroupService } from '../../service/RawMaterialGroupService';
import { SetupOptionsService } from '../../service/SetupOptionsService';
import MaterialUsage from "../../models/MaterialUsage";
import RawMaterialGroup from '../../models/RawMaterialGroup';
import SetupOptions from '../../models/SetupOptions';
import { Link } from 'react-router-dom';
import { FilterMatchMode } from 'primereact/api';
import { Card } from 'primereact/card';

const MaterialUsageView = () => {
    let emptyMaterialUsage: MaterialUsage = {
        usage_Code: 0,
        raw_Material_Group: null,
        rmgIsExcluded: false,
        usage: null,
        stripe_Number: '',
        stripe_Width: null,
        isUsageCodeUsed: false
    };

    const [materialUsages, setMaterialUsages] = useState<MaterialUsage[]>([]);
    const [rawMaterialGroups, setRawMaterialGroups] = useState<RawMaterialGroup[]>([]);
    const [stripeStyles, setStripeStyles] = useState<SetupOptions[]>([]);
    const [loadingMaterialUsages, setLoadingMaterialUsages] = useState(true);
    const [deleteMaterialUsageDialog, setDeleteMaterialUsageDialog] = useState(false);
    const [materialUsage, setMaterialUsage] = useState<MaterialUsage>(emptyMaterialUsage);
    const [rawMaterialGroupDescription, setRawMaterialGroupDescription] = useState<string | undefined>('');
    const [rawMaterialGroupEditDescription, setRawMaterialGroupEditDescription] = useState<string | undefined>('');
    const [stripeNumberDescription, setStripeNumberDescription] = useState<string | undefined>('');
    const [stripeNumberEditDescription, setStripeNumberEditDescription] = useState<string | undefined>('');
    const [editingRows, setEditingRows] = useState({});
    const [globalFilter, setGlobalFilter] = useState<string>('');
    const [filters, setFilters] = useState<DataTableFilterMeta>({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const isNew = useRef(false);
    const toast = useRef<any>(null);
    const dtMaterialUsages = useRef<any>(null);

    useEffect(() => {
        MaterialUsageService.getMaterialUsages().then(data => {
            setMaterialUsages(data);
            setLoadingMaterialUsages(false);
        });
        RawMaterialGroupService.getRawMaterialGroups().then(data => setRawMaterialGroups(data));
        SetupOptionsService.getStripeStyles().then(data => setStripeStyles(data));
    }, []);

    const defaultValues : Record<string, any> = { 
        usageCode: null,
        rawMaterialGroup: null,
        usage: null,
        stripeNumber: null,
        stripeWidth: null 
    };

    const form = useForm({ defaultValues });
    const formEdit = useForm({ mode: "onChange" });
    const errors = form.formState.errors;
    
    const onSubmit = (data: any) => {
        isNew.current = true;
        let _materialUsage = { ...materialUsage };
        _materialUsage.usage_Code = data.usageCode;
        _materialUsage.raw_Material_Group = data.rawMaterialGroup;
        _materialUsage.usage = data.usage;
        _materialUsage.stripe_Number = data.stripeNumber;
        _materialUsage.stripe_Width = data.stripeWidth;
        saveMaterialUsage(_materialUsage);
    };

    const getMaterialUsageErrorMessage = (name: string) => {
        return errors[name] ? <small className="p-error">{(errors[name] as any).message}</small> : <small className="p-error">&nbsp;</small>;
    };

    const getRawMaterialGroupDescription = (id: number, isEdit: boolean) => {
        let _rawMaterialGroups = [ ...rawMaterialGroups ];
        let _rawMaterialGroupDescription = _rawMaterialGroups.filter((v) => v.id === id).map((val) => { return val.description; })[0];
        setTimeout(() => isEdit ? setRawMaterialGroupEditDescription(_rawMaterialGroupDescription) : setRawMaterialGroupDescription(_rawMaterialGroupDescription), 0);
    };

    const getStripeNumberDescription = (setupOption: string, isEdit: boolean) => {
        let _stripeStyles = [ ...stripeStyles ];
        let _stripeStylesDescription = _stripeStyles.filter((v) => v.setupOption === setupOption).map((val) => { return val.setupDescription; })[0];
        setTimeout(() => isEdit ? setStripeNumberEditDescription(_stripeStylesDescription) : setStripeNumberDescription(_stripeStylesDescription), 0);
    };

    const hideDeleteMaterialUsageDialog = () => {
        setDeleteMaterialUsageDialog(false);
    }

    const saveMaterialUsage = (materialUsageData: MaterialUsage) => {
        let _materialUsages = [...materialUsages];

        if (isNew.current) {
            MaterialUsageService.addMaterialUsage(materialUsageData).then((data) => {
                if (data) {
                    _materialUsages.push(data);
                    setMaterialUsages(_materialUsages);
                    toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Material Usage Created', life: 3000 });
                    setMaterialUsage(emptyMaterialUsage);
                    _materialUsages.sort(function (a, b) { return (a.usage_Code as number) - (b.usage_Code as number)});
                    dtMaterialUsages.current.props.value = _materialUsages;
                    form.reset();
                } else {
                    toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Create Material Usage', life: 3000 });
                }
            });
        } else {
            MaterialUsageService.updateMaterialUsage(materialUsageData).then((data) => {
                if (data) {
                    let index = materialUsages.findIndex(r => r.usage_Code === materialUsageData.usage_Code);
                    _materialUsages[index] = materialUsageData;
                    setMaterialUsages(_materialUsages);
                    toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Material Usage Updated', life: 3000 });
                    setMaterialUsage(emptyMaterialUsage);
                } else {
                    toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Material Usage Update Failed', life: 3000 });
                }
            });            
        }
    }

    const confirmDeleteMaterialUsage = (materialUsage: MaterialUsage) => {
        setMaterialUsage(materialUsage);
        setDeleteMaterialUsageDialog(true);
    }

    const deleteMaterialUsage = () => {
        MaterialUsageService.deleteMaterialUsage(materialUsage.usage_Code as number).then((data) => {
            if (data) {
                let _materialUsages = materialUsages.filter(val => val.usage_Code !== materialUsage.usage_Code);
                setMaterialUsages(_materialUsages);
                setDeleteMaterialUsageDialog(false);
                setMaterialUsage(emptyMaterialUsage);
                toast.current.show({ severity: 'success', summary: 'Successful', detail: 'Material Usage Deleted', life: 3000 });
            } else {
                toast.current.show({ severity: 'error', summary: 'Failed', detail: 'Failed to Delete Material Usage', life: 3000 });
            }
        });
    }

    const onRowEditChange = (e: any) => {
        setEditingRows(e.data);
    }

    const setMaterialUsageActiveRowIndex = (rowData: DataTableSelectionSingleChangeEvent<MaterialUsage[]>) => {
        let selectedMaterialUsageIndex = materialUsages.findIndex(v => v.usage_Code === (rowData.value as MaterialUsage).usage_Code);
        let _editingRows = { ...editingRows, ...{ [`${materialUsages[selectedMaterialUsageIndex].usage_Code}`]: true } };
        
        if(Object.keys(_editingRows).length === 1) {
            setEditingRows(_editingRows);
        }
    }

    const rawMaterialGroupEditor = (options: any) => {
        return (
            <Controller 
                name="rawMaterialGroupEdit" 
                control={formEdit.control} 
                defaultValue={options.value}
                render={({ field, fieldState }) => {
                    materialUsage.raw_Material_Group = field.value;
                    getRawMaterialGroupDescription(field.value === undefined ? options.value : field.value, true);
                    
                    return (
                        <>
                            <Dropdown value={fieldState.isDirty ? field.value : options.value} options={rawMaterialGroups} optionLabel="raw_Material_Group" optionValue="id" tooltip={fieldState.invalid ? fieldState.error?.message : rawMaterialGroupEditDescription} tooltipOptions={{ position: 'bottom' }} className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => {materialUsage.raw_Material_Group = e.value; field.onChange(e.value);}} filter filterBy="raw_Material_Group" placeholder="Raw Material Group" virtualScrollerOptions={{ itemSize: 50 }} />
                        </>
                    )} 
                }
            />              
        );
    }

    const usageEditor = (options: any) => {
        return (
            <Controller 
                name="usageEdit" 
                control={formEdit.control} 
                defaultValue={options.value}
                rules={{ 
                    required: 'Yardage is required.',
                    validate: {
                        format: v => validateDecimal(v.toString(), 10, 4) || 'Invalid format.'
                    }
                }}
                render={({ field, fieldState }) => { 
                    materialUsage.usage = field.value;

                    return (
                        <>
                            <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={fieldState.isDirty ? field.value : options.value} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} onBlur={field.onBlur} onValueChange={(e) => field.onChange(e)} inputClassName={classNames({ 'p-invalid border-left-10': fieldState.error })} mode="decimal" useGrouping={false} maxFractionDigits={4} placeholder="Yardage" />
                        </>
                    )} 
                }
            />
        );
    }

    const stripeNumberEditor = (options: any) => {
        return (
            <Controller 
                name="stripeNumberEdit" 
                control={formEdit.control} 
                defaultValue={options.value}
                render={({ field, fieldState }) => {
                    materialUsage.stripe_Number = field.value;
                    getStripeNumberDescription(field.value === undefined? options.value : field.value, true);

                    return (
                        <>
                            <Dropdown value={fieldState.isDirty ? field.value : options.value} options={stripeStyles} optionLabel="setupOption" optionValue="setupOption" tooltip={fieldState.invalid ? fieldState.error?.message : stripeNumberEditDescription} tooltipOptions={{ position: 'bottom' }} className={classNames({ 'p-invalid border-left-10': fieldState.error })} 
                            onChange={(e) => {
                                materialUsage.stripe_Number = e.value; 
                                field.onChange(e.value === undefined ? '' : e.value);
                                }} showClear filter filterBy="value" placeholder="Stripe Number" />
                        </>
                    )} 
                }
            />              
        );        
    }

    const stripeWidthEditor = (options: any) => {
        return (
            <Controller 
                name="stripeWidthEdit" 
                control={formEdit.control} 
                defaultValue={options.value}
                rules={{ 
                    validate: {
                        format: v => v === null ? true : validateDecimal(v.toString(), 10, 3) || 'Invalid format.'
                    }
                }} 
                render={({ field, fieldState }) => {
                    materialUsage.stripe_Width = field.value;

                    return (
                        <>
                            <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={fieldState.isDirty ? field.value : options.value} tooltip={fieldState.error?.message} tooltipOptions={{ position: 'bottom' }} onBlur={field.onBlur} onValueChange={(e) => { field.onChange(e) }} inputClassName={classNames({ 'p-invalid border-left-10': fieldState.error })} mode="decimal" useGrouping={false} maxFractionDigits={3} placeholder="Stripe Width" />
                        </>
                    )} 
                }
            />
        );
    }

    const usageCodeBodyTemplate = (rowData: MaterialUsage) => {
        return rowData.usage_Code;
    }

    const rawMaterialGroupBodyTemplate = (rowData: MaterialUsage) => {
        let _rawMaterialGroup = rawMaterialGroups.filter(val => val.id === rowData.raw_Material_Group).map((e) => { return e.raw_Material_Group });
        return rowData.rmgIsExcluded ? (<span className="text-red-500">{`${_rawMaterialGroup}`}</span>) : _rawMaterialGroup;
    }

    const usageBodyTemplate = (rowData: MaterialUsage) => {
        return rowData.usage;
    }

    const stripeNumberBodyTemplate = (rowData: MaterialUsage) => {
        return rowData.stripe_Number;
    }

    const stripeWidthBodyTemplate = (rowData: MaterialUsage) => {
        return rowData.stripe_Width;
    }

    const rowEditorTemplate = (rowData: MaterialUsage, props: any) => {
        const rowEditor = props.rowEditor;

        if (rowEditor.editing) {
            isNew.current = false;

            return (
                <>
                    <Button icon="p-row-editor-save-icon pi pi-fw pi-save" title="Save" rounded text severity="info" type="submit" onClick={(e) => { 
                        if (!formEdit.getFieldState("usageEdit").invalid && !formEdit.getFieldState("stripeWidthEdit").invalid) {
                            let _materialUsage = { ...materialUsage };
                            _materialUsage.usage_Code = rowData.usage_Code;
                            _materialUsage.isUsageCodeUsed = rowData.isUsageCodeUsed;
                            _materialUsage.rmgIsExcluded = rowData.rmgIsExcluded;
                            saveMaterialUsage(_materialUsage);
                            rowEditor.onSaveClick(e);
                        }
                    }} />
                    
                    <Button icon="p-row-editor-cancel-icon pi pi-fw pi-undo" title="Undo" rounded text severity="danger" onClick={(e) => {
                        formEdit.clearErrors();
                        formEdit.reset();
                        rowEditor.onCancelClick(e);
                    }} />
                </>
            );
        } else {
            return (
                !rowData.isUsageCodeUsed 
                ? 
                    <Link className="pi pi-fw pi-trash rowDelete" title="Delete" onClick={() => confirmDeleteMaterialUsage(rowData)} to={''}></Link>
                : 
                    <></>
            );
        }
    }

    const onGlobalFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        let _filters = { ...filters };

        // @ts-ignore
        _filters['global'].value = value;

        setFilters(_filters);
        setGlobalFilter(value);
    };

    const header = (
        <div className="flex flex-column md:flex-row md:justify-content-end md:align-items-center">
            <IconField iconPosition="left">
                <InputIcon className="pi pi-search" />
                <InputText className="p-inputtext-sm" type="search" autoComplete="off" value={globalFilter} onChange={onGlobalFilterChange} placeholder="Search..." />
            </IconField>
        </div>
    );

    const clearNewMaterialUsage = () => {
        form.reset();
    }

    const deleteMaterialUsageDialogFooter = (
        <>
            <Button label="No" icon="pi pi-times" className="p-button-text" onClick={hideDeleteMaterialUsageDialog} />
            <Button label="Yes" icon="pi pi-check" className="p-button-text" onClick={deleteMaterialUsage} />
        </>
    );
    
    const validateDecimal = (value: string, precision: number, scale: number) => {
        let intPartValid = value.split('.')[0] !== undefined ? value.split('.')[0].length > 0 && value.split('.')[0].length <= precision : false;
        let decPartValid = value.split('.')[1] !== undefined ? value.split('.')[1].length > 0 && value.split('.')[1].length <= scale : true;
        return intPartValid && decPartValid; 
    };

    return (
        <div className="grid-nogutter">
            <div className="col-12">
                <Toast ref={toast} />

                <Card className="layout-main-card border-noround">
                    <Panel header="Add Material Usage" className="addPanel mb-2">
                        <form onSubmit={form.handleSubmit(onSubmit)} className="p-fluid" autoComplete="off">
                            <div className="formgrid grid">
                                <div className="field col-2">
                                    <Controller 
                                        name="usageCode" 
                                        control={form.control} 
                                        rules={{ 
                                            required: 'Usage Code is required.',
                                            validate: {
                                                unique: v => materialUsages.filter(val => val.usage_Code === v).length === 0 || 'Usage Code must be unique.'
                                            } 
                                        }} 
                                        render={({ field, fieldState }) => (
                                            <>
                                                <label htmlFor={field.name} className="font-bold text-xs">Usage Code</label>
                                                <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={field.value} onBlur={field.onBlur} onValueChange={(e) => field.onChange(e)} inputClassName={classNames({ 'p-invalid border-left-10': fieldState.error })} useGrouping={false} autoFocus placeholder="Usage Code" />
                                                {getMaterialUsageErrorMessage('usageCode')}
                                            </>
                                        )} 
                                    />
                                </div>
                                <div className="field col-2">
                                    <Controller 
                                        name="rawMaterialGroup" 
                                        control={form.control} 
                                        rules={{ 
                                            required: 'Raw Material Group is required.' 
                                        }} 
                                        render={({ field, fieldState }) => (
                                            <>
                                                <label htmlFor={field.name} className="font-bold text-xs">Raw Material Group</label>
                                                <Dropdown inputId={field.name} value={field.value} options={rawMaterialGroups} optionLabel="raw_Material_Group" optionValue="id" className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => {getRawMaterialGroupDescription(e.value, false); field.onChange(e.value);}} showClear filter filterBy="raw_Material_Group" placeholder="Raw Material Group" virtualScrollerOptions={{ itemSize: 50 }} />
                                                {field.value !== null ? <small className="text-primary">{rawMaterialGroupDescription}</small> : <></>}
                                                {getMaterialUsageErrorMessage('rawMaterialGroup')}
                                            </>
                                        )} 
                                    />                                    
                                </div>                                
                                <div className="field col-2">
                                    <Controller 
                                        name="usage" 
                                        control={form.control} 
                                        rules={{ 
                                            required: 'Yardage is required.',
                                            validate: {
                                                format: v => validateDecimal(v.toString(), 10, 4) || 'Invalid format.'
                                            }
                                        }}
                                        render={({ field, fieldState }) => (
                                            <>
                                                <label htmlFor={field.name} className="font-bold text-xs">Yardage</label>
                                                <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={field.value} onBlur={field.onBlur} onValueChange={(e) => field.onChange(e)} inputClassName={classNames({ 'p-invalid border-left-10': fieldState.error })} mode="decimal" useGrouping={false} maxFractionDigits={4} placeholder="Yardage" />
                                                {getMaterialUsageErrorMessage('usage')}
                                            </>
                                        )} 
                                    />                                    
                                </div>
                                <div className="field col-2">
                                    <Controller 
                                        name="stripeNumber" 
                                        control={form.control}
                                        render={({ field, fieldState }) => (
                                            <>
                                                <label htmlFor={field.name} className="font-bold text-xs">Stripe Number</label>
                                                <Dropdown inputId={field.name} value={field.value} options={stripeStyles} optionLabel="setupOption" optionValue="setupOption" className={classNames({ 'p-invalid border-left-10': fieldState.error })} onChange={(e) => {getStripeNumberDescription(e.value, false); field.onChange(e.value);}} showClear filter filterBy="setupOption" placeholder="Stripe Number" virtualScrollerOptions={{ itemSize: 50 }} />
                                                {(field.value !== null && field.value !== undefined && field.value !== '') ? <small className="text-primary">{stripeNumberDescription}</small> : <></>}
                                                {getMaterialUsageErrorMessage('stripeNumber')}
                                            </>
                                        )} 
                                    />                                      
                                </div>
                                <div className="field col-2">
                                    <Controller 
                                        name="stripeWidth" 
                                        control={form.control} 
                                        rules={{ 
                                            validate: {
                                                format: v => v === null ? true : validateDecimal(v.toString(), 10, 3) || 'Invalid format.'
                                            }
                                        }} 
                                        render={({ field, fieldState }) => (
                                            <>
                                                <label htmlFor={field.name} className="font-bold text-xs">Stripe Width</label>
                                                <InputNumber id={field.name} name={field.name} inputRef={field.ref} value={field.value} onBlur={field.onBlur} onValueChange={(e) => field.onChange(e)} inputClassName={classNames({ 'p-invalid border-left-10': fieldState.error })} mode="decimal" useGrouping={false} maxFractionDigits={3} placeholder="Stripe Width" />
                                                {getMaterialUsageErrorMessage('stripeWidth')}
                                            </>
                                        )} 
                                    />  
                                </div>
                                <div className="field field-buttons col-2 pt-4">
                                    <Button icon="pi pi-save" title="Save" rounded text severity="info" type="submit" />
                                    <Button icon="pi pi-undo" title="Undo" rounded text severity="danger" type="button" onClick={clearNewMaterialUsage} />
                                </div>
                            </div>
                        </form>
                    </Panel>

                    <DataTable ref={dtMaterialUsages} value={materialUsages} selectionMode="single" onSelectionChange={setMaterialUsageActiveRowIndex}
                        dataKey="usage_Code" size="small" rowHover={true} loading={loadingMaterialUsages}
                        sortField="usage_Code"
                        scrollable scrollHeight="calc(100vh - 25.1rem)"
                        paginator rows={25} rowsPerPageOptions={[5, 10, 25]} 
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Showing {first} to {last} of {totalRecords} material usages"
                        editingRows={editingRows} editMode="row" onRowEditChange={onRowEditChange}
                        filters={filters} globalFilterFields={['usage_Code', 'rmgDescription', 'usage', 'stripe_Number', 'stripe_Width']}
                        emptyMessage="No material usages found." header={header}>
                        <Column field="usage_Code" header="Usage Code" body={usageCodeBodyTemplate} sortable align="center" alignHeader="center" headerStyle={{ width: '17%', minWidth: '10rem' }}></Column>
                        <Column field="raw_Material_Group" header="Raw Material Group" body={rawMaterialGroupBodyTemplate} editor={(options:ColumnEditorOptions) => rawMaterialGroupEditor(options)} sortable align="center" alignHeader="center" headerStyle={{ width: '17%', minWidth: '10rem' }}></Column>
                        <Column field="rmgDescription" style={{'display': 'none'}} header="Raw Material Group"></Column>
                        <Column field="usage" header="Yardage" body={usageBodyTemplate} editor={(options:ColumnEditorOptions) => usageEditor(options)} sortable align="center" alignHeader="center" headerStyle={{ width: '17%', minWidth: '8rem' }}></Column>
                        <Column field="stripe_Number" header="Stripe Number" body={stripeNumberBodyTemplate} editor={(options:ColumnEditorOptions) => stripeNumberEditor(options)} sortable align="center" alignHeader="center" headerStyle={{ width: '17%', minWidth: '10rem' }}></Column>
                        <Column field="stripe_Width" header="Stripe Width" body={stripeWidthBodyTemplate} editor={(options:ColumnEditorOptions) => stripeWidthEditor(options)} sortable align="center" alignHeader="center" headerStyle={{ width: '17%', minWidth: '10rem' }}></Column>
                        <Column rowEditor body={rowEditorTemplate} headerStyle={{ width: '10%', minWidth: '8rem' }} bodyStyle={{ textAlign: 'center' }}></Column>
                    </DataTable>
                </Card>

                <Dialog visible={deleteMaterialUsageDialog} style={{ width: '450px' }} header="Confirm" modal footer={deleteMaterialUsageDialogFooter} onHide={hideDeleteMaterialUsageDialog}>
                    <div className="flex align-items-center justify-content-center">
                        <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                        {materialUsage && <span>Are you sure you want to delete <b>{materialUsage.usage_Code}</b>?</span>}
                    </div>
                </Dialog>
            </div>
        </div>
    );
}

export default MaterialUsageView;